import { createContext, useContext, type ReactNode } from "react";
import { cn } from "@repo/blacksmith-ui/cn";

/** Title-case label above a thread card's title; shared with ProofPanel. */
export const CARD_EYEBROW_CLASS =
  "type-chat-prose-medium text-neutral-500 dark:text-neutral-400";

/**
 * `no-type-headers` drops the eyebrow row and moves the icon in front of the
 * title, so the card leads with its name instead of its kind.
 */
export type ChatCardStyle = "with-type-headers" | "no-type-headers";

const ChatCardStyleContext = createContext<ChatCardStyle>("no-type-headers");

export const ChatCardStyleProvider = ChatCardStyleContext.Provider;

export function useChatCardStyle(): ChatCardStyle {
  return useContext(ChatCardStyleContext);
}

/**
 * Shared frame for rich cards in the session transcript.
 *
 * `as="span"` renders every wrapper as a span for cards that live inside
 * SessionMarkdown's inline flow (e.g. the canvas card), where a div would be
 * invalid inside a paragraph.
 */
export function ChatCardFrame({
  as,
  icon,
  eyebrow,
  title,
  titleTrailing,
  titleId,
  wrapTitle = false,
  keepTypeHeader = false,
  headerAction,
  children,
  unpadded = false,
  interactive = false,
  footerStart,
  footerAction,
  wrapFooter = false,
  dividedFooter = false,
  className,
  contentClassName,
  ...rest
}: {
  as?: "div" | "span";
  /** 16px icon, sized by the caller (e.g. `className="size-4"`). */
  icon: ReactNode;
  eyebrow: ReactNode;
  /** Omit when the body already leads with the card's name (e.g. McpServerSummary). */
  title?: ReactNode;
  /** Identifier (e.g. `web#5498`) trailing the title; `no-type-headers` only. */
  titleTrailing?: ReactNode;
  /** Id on the title, so a footer control can name itself via aria-labelledby. */
  titleId?: string;
  /** Let a sentence-length title wrap instead of truncating to one line. */
  wrapTitle?: boolean;
  /** Hold the eyebrow even under `no-type-headers` (the kind is the card's subject). */
  keepTypeHeader?: boolean;
  /** Trailing header slot (status chip, dismiss button, …). */
  headerAction?: ReactNode;
  /** Body content under the title. */
  children?: ReactNode;
  /** Skip the body padding for full-bleed content like row lists. */
  unpadded?: boolean;
  /** Apply the card hover state. */
  interactive?: boolean;
  footerStart?: ReactNode;
  /** CTA pinned to the footer's right edge. */
  footerAction?: ReactNode;
  /** Drop the CTA onto its own row when it and footerStart can't share one, instead of squeezing footerStart. */
  wrapFooter?: boolean;
  /** Rule the footer off with even padding, for unpadded cards whose body ends in a full-bleed row. */
  dividedFooter?: boolean;
  className?: string;
  contentClassName?: string;
} & Record<string, unknown>) {
  const Box = as ?? "div";
  const hasFooter = Boolean(footerStart || footerAction);
  const hideTypeHeader =
    useChatCardStyle() === "no-type-headers" && !keepTypeHeader;
  const hideHeaderRow = hideTypeHeader && !title && !headerAction;
  const titleClassName = cn(
    "block type-chat-prose-medium text-neutral-900 dark:text-neutral-100",
    wrapTitle ? "break-words" : "truncate",
  );
  return (
    <Box
      data-chat-card-frame
      data-chat-card-interactive={interactive ? true : undefined}
      className={cn(
        "block overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]",
        interactive &&
          "group/chat-card cursor-pointer transition-colors hover:bg-[var(--color-surface-hover)]",
        className,
      )}
      {...rest}
    >
      {hideHeaderRow ? null : (
        <Box
          className={cn(
            "flex min-h-9 items-center px-4 pt-2.5 pb-1",
            hideTypeHeader ? "gap-2" : "gap-1",
          )}
        >
          <Box className="flex shrink-0 items-center text-neutral-500 dark:text-neutral-400">
            {icon}
          </Box>
          {hideTypeHeader ? (
            <Box className="flex min-w-0 flex-1 items-baseline gap-1.5">
              <Box id={titleId} className={cn("min-w-0", titleClassName)}>
                {title}
              </Box>
              {titleTrailing ? (
                <Box className={cn("shrink-0", CARD_EYEBROW_CLASS)}>
                  {titleTrailing}
                </Box>
              ) : null}
            </Box>
          ) : (
            <Box className={cn("block min-w-0 flex-1", CARD_EYEBROW_CLASS)}>
              {eyebrow}
            </Box>
          )}
          {headerAction ? (
            // Negative margin keeps a taller action from
            // growing the header, so cards that toggle one in don't shift the body.
            <Box className="-my-1 flex shrink-0 items-center gap-1">
              {headerAction}
            </Box>
          ) : null}
        </Box>
      )}
      {(title && !hideTypeHeader) || children ? (
        <Box
          className={cn(
            "block",
            !unpadded && "flex flex-col gap-1 px-4",
            !unpadded && (hideHeaderRow ? "pt-4" : "pt-1"),
            !unpadded && (hasFooter ? "pb-1" : "pb-4"),
            contentClassName,
          )}
        >
          {title && !hideTypeHeader ? (
            <Box
              id={titleId}
              className={cn(titleClassName, unpadded && "px-4 pt-1 pb-1")}
            >
              {title}
            </Box>
          ) : null}
          {children}
        </Box>
      ) : null}
      {hasFooter ? (
        <Box
          className={cn(
            "flex items-center justify-between gap-2 px-4",
            dividedFooter
              ? "border-t border-[var(--color-border)] py-3"
              : "pt-1 pb-3",
            wrapFooter && "flex-wrap",
          )}
        >
          <Box
            className={cn(
              "flex min-w-0 items-center gap-2",
              // flex-auto keeps footerStart's content width as its basis so the
              // action wraps below it instead of footerStart shrinking to zero.
              wrapFooter ? "flex-auto" : "flex-1",
            )}
          >
            {footerStart}
          </Box>
          {footerAction ? (
            <Box className="ml-auto flex shrink-0 items-center gap-1">
              {footerAction}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
